import { cn } from "@/lib/utils";
import { useCategoryIcon, useCategoryIconUrl } from "@/lib/category-icon";
import { optimizeImage } from "@/lib/image";

type Props = {
  psId: number | null | undefined;
  /** Size classes for the rendered icon (applied to both img and svg). */
  className?: string;
  /**
   * Pixel width to request from the image transform endpoint. Defaults to 64,
   * which is plenty for the small (16–20px) nav/breadcrumb icons even at high
   * DPR. Pass a larger value where the icon renders big (e.g. category hero).
   */
  width?: number;
};

/**
 * Renders a category's icon. Prefers the image stored in the DB
 * (categories.icon_url); falls back to a deterministic Lucide icon when no
 * image is available (e.g. some subcategories).
 */
export function CategoryIcon({ psId, className, width = 64 }: Props) {
  const url = useCategoryIconUrl(psId);
  const LucideFallback = useCategoryIcon(psId);
  if (url) {
    return (
      <img
        src={optimizeImage(url, { width })}
        alt=""
        aria-hidden
        width={width}
        height={width}
        loading="lazy"
        decoding="async"
        className={cn("shrink-0 object-contain", className)}
      />
    );
  }
  if (LucideFallback) return <LucideFallback className={cn("shrink-0", className)} />;
  return null;
}